/**
 * FormField component - label + input wrapper with accessibility.
 * @module components/ui/Form/FormField
 */

import React, { type ReactElement, type ReactNode } from "react";

import { useEditLock } from "@/components/ui/EditLock";
import { HelpTip } from "@/components/ui/HelpTip";
import { Icon } from "@/components/ui/Icon";

export interface FormFieldProps {
  /** Field label content */
  label: ReactNode;
  /** Unique ID for the input (used for label association) */
  htmlFor: string;
  /** The form control element */
  children: ReactNode;
  /** Help text displayed below the input */
  helpText?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Additional CSS classes for the wrapper */
  className?: string;
  /**
   * What is wrong with this field, in plain language. Rendered beside the
   * control and left there until it is resolved.
   */
  error?: string;
}

/**
 * FormField component that wraps an input with a label and optional tooltip help text.
 */
export function FormField({
  label,
  htmlFor,
  children,
  helpText = undefined,
  required = false,
  className = "",
  error = undefined,
}: FormFieldProps) {
  // A grid cell defaults to min-width: min-content, so without min-w-0 a long
  // label or a nowrap control widens the whole track.
  // A value that cannot be entered has nothing to be required of.
  const { locked } = useEditLock();
  const wrapperClass = `flex min-w-0 flex-col${className ? ` ${className}` : ""}`;
  const helpId = helpText ? `${htmlFor}-help` : undefined;
  const errorId = error ? `${htmlFor}-error` : undefined;
  // Validated before its props are read: a conditionally rendered control
  // passes `null` here, and an unguarded `.props` on it takes the tree down.
  const childElement = React.isValidElement(children)
    ? (children as ReactElement<{
        "aria-describedby"?: string;
        "aria-invalid"?: boolean;
      }>)
    : null;
  const helpButtonLabel = typeof label === "string" ? `Help for ${label}` : "Field help";

  // The control is told about both its help text and its error, so a screen
  // reader hears why the field is refused rather than only that it is.
  const describedBy = [childElement?.props?.["aria-describedby"], helpId, errorId]
    .filter(Boolean)
    .join(" ");
  const child =
    (helpText || error) && childElement
      ? React.cloneElement(childElement, {
          "aria-describedby": describedBy || undefined,
          ...(error ? { "aria-invalid": true } : {}),
        })
      : children;

  return (
    <div className={wrapperClass || undefined}>
      {/* The help icon follows the label and never drops to a line of its
          own. The row keeps its place above the control; an error or a growing
          control flows below it, so neither moves a sibling's label. */}
      <div className="mb-1 flex min-h-6 flex-nowrap items-center gap-1">
        <label htmlFor={htmlFor} className="label min-w-0 text-md font-medium leading-6">
          {label}
          {required && !locked && <span className="text-error-strong ml-1">*</span>}
        </label>
        {helpText && <HelpTip text={helpText} label={helpButtonLabel} describedBy={helpId} />}
      </div>
      {child}
      {helpText && (
        <span className="sr-only" id={helpId}>
          {helpText}
        </span>
      )}
      {/* Text and an icon, never colour alone, and inline rather than in a
          toast: an error the person has to fix cannot dismiss itself while
          they are reading it. */}
      {error && (
        <p id={errorId} className="mt-1 flex items-center gap-1 text-md text-error-strong">
          <Icon name="warning-circle" aria-hidden />
          {error}
        </p>
      )}
    </div>
  );
}

export default FormField;
